import styled, { useTheme } from "styled-components/native";
import { Spacer, VDBText } from "@components/atoms";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { SpaceType } from "@components/atoms/Spacer/types";
import GoBackComponent from "@components/molecules/GoBackComponent/GoBackComponent";
import React, { useState } from "react";
import { useNavigation } from "@react-navigation/native";
import { useTranslation } from "react-i18next";
import { Platform } from "react-native";
import Popup from "@components/molecules/PopUp";
import { messageType } from "@components/molecules/InputPopUp/types";
import { ButtonType } from "@components/atoms/Button/types";
import { useSelector } from "react-redux";
import { screenWidth } from "@utils/constants";
import { getDrawerWidth } from "@navigations/utils/constants";
import Routes from "@navigations/Routes";
import { navigate } from "@navigations/utils/RootNavigation";
import { css } from "styled-components/native";

type WebHeaderProps = {
  title?: string;
  hideBack?: boolean;
  LeftItems?: any;
  RightItems?: any;
  isV2?: boolean;
  maxWidth?: number;
  titleIcon?: React.ReactNode;
  mediaCount?: number;
  addPadding?: boolean;
  applyGap?: boolean;
  hasUnsavedChanges?: boolean;
  showBackDivider?: boolean;
  testID?: string;
  dataTestId?: string;
  isFromDeeplink?: boolean;
  route_url?: string;
};

const WebPageHeader = ({
  title,
  LeftItems,
  RightItems,
  titleIcon,
  hideBack = false,
  isV2,
  maxWidth,
  mediaCount,
  hasUnsavedChanges = false,
  showBackDivider = false,
  addPadding = false,
  applyGap = false,
  testID,
  dataTestId,
  isFromDeeplink,
  route_url,
}: WebHeaderProps) => {
  const typography = useTypographyStore();
  const theme = useTheme();
  const navigation = useNavigation();
  const { t } = useTranslation();
  const [showConfirmPopup, setShowConfirmPopup] = useState(false);

  const handleGoBack = () => {
    if (hasUnsavedChanges || (isFromDeeplink && route_url)) {
      setShowConfirmPopup(true);
    } else {
      if (Platform.OS === "web") {
        localStorage.removeItem("jcSelectedOption");
        localStorage.removeItem("personalization");
      }
      navigation.goBack();
    }
  };
  const isSplitFilterVisible = useSelector(
    (state: any) => state.appState?.isFavCollectionVisible
  );
  const drawerWidth = getDrawerWidth(screenWidth, theme);

  if (!(theme.isLargeScreen && Platform.OS === "web")) return null;

  return (
    <>
      <Container
        isV2={isV2}
        maxWidth={maxWidth || 0}
        mediaCount={mediaCount}
        applyGap={applyGap}
      >
        <LeftView>
          {((!hideBack && navigation.canGoBack()) || isFromDeeplink) && (
            <GoBackComponent
              title={t("productDetailPage.goBack.text")}
              action={handleGoBack}
              testID={testID}
              dataTestId={dataTestId}
            />
          )}
          {!isSplitFilterVisible && addPadding && (
            <>
              <Spacer size={drawerWidth - 17} />
            </>
          )}
          {showBackDivider &&
            ((!hideBack && navigation.canGoBack()) || isFromDeeplink) && (
              <TitleDivider />
            )}
          {titleIcon}
          {title && (
            <VDBText color={theme["core-05"]} type={typography["H2-M"]}>
              {title}
            </VDBText>
          )}
          {LeftItems && <LeftItems />}
        </LeftView>

        <RightView>{RightItems && <RightItems />}</RightView>
      </Container>

      {showConfirmPopup && (
        <Popup
          type={messageType.Warning}
          styleKey="Body-R"
          title={t("bulkRequest.deleteIndividual.modal.title.text")}
          buttons={[
            {
              type: ButtonType.primary,
              title: t("bmh.canRequestPopUp.button.title.text"),
              onPressAction: () => {
                setShowConfirmPopup(false);
                if (isFromDeeplink && route_url) {
                  if (Platform.OS === "web") {
                    window.location.href =
                      window.location.origin + "/" + route_url;
                  } else {
                    navigate(Routes.AppNavigator, {
                      screen: Routes.HomeNavigator,
                      params: {
                        screen: Routes.HomeScreen,
                        params: {
                          iframeUrl: window.location.origin + "/" + route_url,
                        },
                      },
                      initial: false,
                    });
                  }
                } else {
                  navigation.goBack();
                }
              },
            },
            {
              type: ButtonType.secondary,
              title: t("bmh.popUp.button.dismiss.text"),
              onPressAction: () => {
                setShowConfirmPopup(false);
              },
            },
          ]}
          hasChildren={true}
        >
          <VDBText>
            {t("requestHistoryDetail.modifyCancel.requestPopup.title.text")}
          </VDBText>
        </Popup>
      )}
    </>
  );
};

const Container = styled.View<{
  isV2?: boolean;
  mediaCount?: number;
  maxWidth: number;
  applyGap: boolean;
}>`
  height: ${({ isV2 }) => (isV2 ? "auto" : 56)}px;
  background-color: ${({ theme, isV2 }) =>
    isV2 ? "transparent" : theme["core-01"]};
  width: 100%;
  ${({ isV2 }) =>
    isV2
      ? `
      padding: ${SpaceType.Basic}px ${SpaceType.Tiny}px;
    `
      : `padding: ${SpaceType.Basic}px ${SpaceType.Big}px;`}
  justify-content: space-between;
  align-items: center;
  flex-direction: row;
  margin: 0 auto;
  padding-top: ${({ isV2 }) => (isV2 ? SpaceType.Double : SpaceType.Basic)}px;
  border-bottom-width: 1px;
  border-color: ${({ theme, isV2 }) =>
    isV2 ? "transparent" : theme["core-03"]};
  max-width: ${({ theme, isV2, maxWidth, mediaCount }) =>
    maxWidth
      ? `${maxWidth}px;`
      : isV2
      ? theme.isLargeScreen
        ? (mediaCount ?? 0) > 1
          ? "1120px"
          : "920px"
        : "700px"
      : "100%"};
  ${({ applyGap }) =>
    applyGap &&
    css`
      gap: 8px;
    `}
`;

const LeftView = styled.View`
  flex-direction: row;
  align-items: center;
  gap: ${SpaceType.Double}px;
`;

const TitleDivider = styled.View`
  width: 1px;
  height: 24px;
  background-color: ${({ theme }) => theme["core-03"]};
`;
const RightView = styled.View`
  flex-direction: row;
  align-items: center;
  gap: ${SpaceType.Double}px;
`;

export default WebPageHeader;
